"use client";

import DeleteConfirmationDialog from "../delete-confirmation-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownSettingsButton,
} from "../ui/dropdown";

import { useState } from "react";
import { FiClock, FiEdit2, FiPrinter, FiTrash2 } from "react-icons/fi";

import { AuditTrailDialog } from "@/components/care-plan/audit-trail";
import { CarePlanPreviewCard } from "@/components/care-plan/care-plan-preview-card";
import { useProgressNavigation } from "@/components/progress-bar";

import type { CarePlanSummary } from "@/types/care-plan";

import type { CarePlanSize } from "./types";
import { useDeleteCarePlanMutation } from "@/query/patient/care-plan";

interface ReadOnlyCarePlanCardProps {
  carePlan: CarePlanSummary;
  patientId: string;
  children?: React.ReactNode | ((contentClassName: string) => React.ReactNode);
  orientation?: "horizontal" | "vertical";
  size?: CarePlanSize;
  onPrint?: () => void;
}

export function ReadOnlyCarePlanCard({
  carePlan,
  patientId,
  children,
  size = "md",
  onPrint,
}: ReadOnlyCarePlanCardProps) {
  const { navigateWithProgress } = useProgressNavigation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isAuditTrailOpen, setIsAuditTrailOpen] = useState(false);
  const deleteCarePlanMutation = useDeleteCarePlanMutation();

  const actionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <DropdownSettingsButton size={size === "sm" ? "sm" : "base"} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          onClick={() => {
            navigateWithProgress(
              `/patient/${patientId}/care-plan/${carePlan.id}`,
            );
          }}
          icon={<FiEdit2 className="size-3" />}
        >
          Edit
        </DropdownMenuItem>
        {onPrint && (
          <DropdownMenuItem
            onClick={onPrint}
            icon={<FiPrinter className="size-3" />}
          >
            Print
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          icon={<FiClock className="size-3" />}
          onClick={() => setIsAuditTrailOpen(true)}
        >
          Show history
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setIsDeleteConfirmOpen(true);
          }}
          icon={<FiTrash2 className="size-3 text-status-error-text" />}
          className="text-status-error-text"
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <CarePlanPreviewCard
        name={carePlan.name}
        stepCount={carePlan.stepCount}
        completedStepCount={carePlan.completedStepCount}
        tags={carePlan.tags ?? []}
        size={size}
        actions={actionsMenu}
      >
        {children}
      </CarePlanPreviewCard>
      <DeleteConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        setIsOpen={setIsDeleteConfirmOpen}
        onConfirm={() => {
          deleteCarePlanMutation.mutate(carePlan.id);
        }}
        title="Delete Care Plan"
        subtitle="Are you sure you want to delete this care plan? This action cannot be undone."
      />
      <AuditTrailDialog
        carePlanId={carePlan.id}
        patientId={patientId}
        open={isAuditTrailOpen}
        onOpenChange={setIsAuditTrailOpen}
      />
    </>
  );
}
